// src/pages/shop/ShopHome.jsx
// src/pages/shop/ShopHome.jsx
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shop as shopApi, sales as salesApi, profiles as profilesApi, products as productsApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { useToast } from '../../hooks/useToast';
import { formatMoney } from '../../data/currencies';
import { useMyCurrency, useMyShop, setMyShop } from '../../hooks/useMyShop';
import { errorMessage } from '../../utils/errorMessage';
import { isModuleOn, MODULES, canSeeModule } from '../../config/modules';

export default function ShopHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isOwner, can } = useAuth();
  const { show, ToastView } = useToast();
  const navItems = useShopNav();
  const currency = useMyCurrency();

  const [shop, setShop] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const sees = (key) => canSeeModule(shop, key, { isOwner, can });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [s, salesData] = await Promise.all([
        shopApi.my().catch(() => null),
        salesApi.list(1).catch(() => ({ revenue: 0, profit: 0, count: 0 })),
      ]);
      setShop(s);
      setStats(salesData);
    } catch (e) {}
    setLoading(false);
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;
  if (!shop) return <CreateShopPrompt onDone={load} />;

  const today = stats?.revenue || 0;
  const todayProfit = stats?.profit || 0;
  const todaySales = stats?.count || 0;

  const copyId = async () => {
    try {
      await navigator.clipboard?.writeText(shop.id);
      show('ID скопирован');
    } catch (e) {
      show(`ID: ${shop.id}`);
    }
  };

  return (
    <div className="page">
      {ToastView}

      <div style={{ padding: '20px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="page-subtitle">МАГАЗИН</p>
            <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--fg-strong)' }}>
              {shop.name}
            </h1>
          </div>
          <button onClick={() => navigate('/settings')}
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)', padding: 8,
              color: 'var(--fg-muted)', cursor: 'pointer',
              flexShrink: 0,
              width: 38, height: 38,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name="settings" size={18} />
          </button>
        </div>

        {/* Hero выручки — только для владельца или сотрудника с analytics */}
        {sees('analytics') && (
          <div className="hero-shop" style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: '0.08em' }}>{t('common.today').toUpperCase()}</p>
            <p className="hero-num" style={{ marginTop: 6 }}>
              {formatMoney(today, currency)}
            </p>
            <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
              <div>
                <p style={{ fontSize: 11, opacity: 0.7, fontWeight: 600 }}>{t('shopHome.todayProfit')}</p>
                <p className="num" style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{formatMoney(todayProfit, currency)}</p>
              </div>
              <div>
                <p style={{ fontSize: 11, opacity: 0.7, fontWeight: 600 }}>{t('shopHome.todaySales')}</p>
                <p className="num" style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{todaySales}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Главное действие — Касса (только для владельца или сотрудника с cashier) */}
        {sees('cashier') && (
          <button
            onClick={() => navigate('/cashier')}
            style={{
              width: '100%',
              display: 'flex', alignItems: 'center', gap: 14,
              padding: 18,
              background: 'var(--brand-primary)',
              border: 'none', borderRadius: 'var(--r-md)',
              color: '#fff', cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(99, 102, 241, 0.25)',
              marginBottom: 12,
              fontFamily: 'var(--font-display)',
              transition: 'transform 120ms var(--ease-spring)',
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
            onMouseUp={(e) => e.currentTarget.style.transform = ''}
            onMouseLeave={(e) => e.currentTarget.style.transform = ''}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 'var(--r-sm)',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon name="cashier" size={24} stroke={2} />
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <p style={{ fontSize: 16, fontWeight: 800 }}>{t('shopHome.viewCashier')}</p>
              <p style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{t('cashier.subtitle')}</p>
            </div>
            <Icon name="chevronRight" size={18} stroke={2.5} />
          </button>
        )}

        {/* Грид быстрых действий — каждый по своему праву */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {sees('products') && (
            <QuickAction iconName="package" label={t('nav.products')} onClick={() => navigate('/products')} />
          )}
          {sees('stocktake') && (
            <QuickAction iconName="list" label={t('inventory.title')} onClick={() => navigate('/inventory')} />
          )}
          {sees('categories') && (
            <QuickAction iconName="folder" label={t('categories.title')} onClick={() => navigate('/categories')} />
          )}
          {sees('analytics') && (
            <QuickAction iconName="chart" label={t('analytics.title')} onClick={() => navigate('/analytics')} />
          )}
          {sees('ai') && (
            <QuickAction iconName="sparkles" label={t('shopHome.aiTools')} onClick={() => navigate('/ai-tools')} />
          )}
          {sees('aiAssistant') && (
            <QuickAction iconName="sparkles" label={t('shopHome.aiAssistant')} onClick={() => navigate('/ai-assistant')} />
          )}
          {sees('counterparties') && (
            <QuickAction iconName="truck" label={t('counterparties.title')} onClick={() => navigate('/counterparties')} />
          )}
          {sees('retailDebt') && (
            <QuickAction iconName="users" label={t('clients.title')} onClick={() => navigate('/debtors')} />
          )}
          {sees('marking') && (
            <QuickAction iconName="qr" label={t('marking.checkerTitle')} onClick={() => navigate('/marking-checker')} />
          )}
          {sees('waiter') && (
            <QuickAction iconName="cashier" label={t('modules.waiter')} onClick={() => navigate('/waiter')} />
          )}
          {sees('kitchen') && (
            <QuickAction iconName="list" label={t('modules.kitchen')} onClick={() => navigate('/kitchen')} />
          )}
          {sees('wholesale') && (
            <QuickAction iconName="truck" label={t('modules.wholesale')} onClick={() => navigate('/wholesale-sale')} />
          )}
          {sees('purchases') && (
            <QuickAction iconName="download" label={t('purchase.title')} onClick={() => navigate('/purchase')} />
          )}
          {sees('perishable') && (
            <PerishableTile onClick={() => navigate('/perishable')} />
          )}
          {isOwner && (
            <QuickAction iconName="list" label={t('audit.title')} onClick={() => navigate('/audit')} />
          )}
          <QuickAction iconName="share" label={t('common.share') + ' ID'} onClick={copyId} />
        </div>

        {/* ID магазина */}
        <div className="card" style={{ marginBottom: 16, cursor: 'pointer' }} onClick={copyId}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 'var(--r-sm)',
              background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon name="qr" size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 600, letterSpacing: '0.05em' }}>ID МАГАЗИНА</p>
              <p style={{ fontSize: 18, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--brand-primary)', marginTop: 2, letterSpacing: 1 }}>
                {shop.id}
              </p>
            </div>
            <Icon name="copy" size={18} color="var(--fg-faint)" />
          </div>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 8 }}>
            Покупатели введут этот ID, чтобы добавить вас в избранное и копить баллы
          </p>
        </div>
      </div>

      <BottomNav items={navItems} />
    </div>
  );
}

// Плитка «Скоропорт»: меняет цвет по ближайшему истекающему сроку.
function PerishableTile({ onClick }) {
  const { t } = useTranslation();
  const [level, setLevel] = useState('none');
  useEffect(() => {
    let alive = true;
    productsApi.expiring()
      .then(r => { if (alive) setLevel(r.level || 'none'); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const color = level === 'red' ? 'var(--danger)'
    : level === 'yellow' ? 'var(--warning, #d98324)' : null;
  return (
    <button
      onClick={onClick}
      className="action-tile"
      style={color ? { borderColor: color, background: level === 'red' ? 'var(--danger-soft, #fde8e8)' : 'var(--warning-soft, #fdf3e4)' } : undefined}
    >
      <div className="ico" style={color ? { color } : undefined}>
        <Icon name="alert" size={22} />
      </div>
      <p style={color ? { color, fontWeight: 700 } : undefined}>{t('perishable.title', 'Скоропорт')}</p>
    </button>
  );
}

function QuickAction({ iconName, label, onClick }) {
  return (
    <button onClick={onClick} className="action-tile">
      <div className="ico">
        <Icon name={iconName} size={22} />
      </div>
      <p>{label}</p>
    </button>
  );
}

// ─── Создание магазина (если первый раз) ───
function CreateShopPrompt({ onDone }) {
  const { t } = useTranslation();
  const { show, ToastView } = useToast();
  const { refreshUser } = useAuth();
  const nameRef = useRef();
  const descRef = useRef();
  const refRef = useRef();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [profileList, setProfileList] = useState([]);
  const [profileId, setProfileId] = useState(null);

  useEffect(() => {
    profilesApi.list()
      .then(r => {
        const items = (r && r.items) || [];
        setProfileList(items);
        if (items.length) setProfileId(items[0].id); // первый = рекомендуемый
      })
      .catch(() => setProfileList([]));
  }, []);

  const create = async () => {
    const name = nameRef.current?.value?.trim();
    if (!name) return setError(t('settings.errorNameRequired'));
    setCreating(true); setError('');
    try {
      const refCode = refRef.current?.value?.trim().toUpperCase() || '';
      const result = await shopApi.create({
        name,
        description: descRef.current?.value?.trim() || '',
        category: 'other',
        ...(profileId ? { profileId } : {}),
        ...(refCode ? { referralCode: refCode } : {}),
      });
      // Сразу кладём созданный магазин в кэш (с правильным moduleConfig из профиля)
      // и перечитываем пользователя — чтобы роль стала owner и интерфейс
      // владельца появился без перелогина.
      setMyShop(result);
      await refreshUser();
      // Если рефкод сработал — покажем тост о начислении дней
      const sub = result?.subscription;
      if (sub && sub.source === 'referral' && sub.plan?.name) {
        const days = sub.expiresAt
          ? Math.max(0, Math.round((new Date(sub.expiresAt) - new Date()) / 86400000))
          : null;
        const planName = sub.plan.name;
        const msg = days
          ? t('shop.referralBonusGranted', { plan: planName, days })
          : t('shop.referralBonusGrantedNoDays', { plan: planName });
        show(msg, 'success');
        // Чуть подождём чтобы юзер увидел тост перед переходом
        setTimeout(onDone, 1600);
      } else {
        onDone();
      }
    } catch (e) { setError(errorMessage(e, t)); }
    setCreating(false);
  };

  return (
    <div className="page" style={{ padding: 20 }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, marginTop: 40, marginBottom: 8, letterSpacing: '-0.02em' }}>
        {t('shop.createShop')}
      </h1>
      <p style={{ fontSize: 14, color: 'var(--fg-muted)', marginBottom: 24 }}>
        {t('shop.createShopHint')}
      </p>

      <div className="input-group">
        <label>{t('shop.shopName')} *</label>
        <input ref={nameRef} className="input" placeholder={t('shop.shopNamePlaceholder')} />
      </div>
      <div className="input-group">
        <label>{t('shop.shopDescription')}</label>
        <textarea ref={descRef} className="textarea" placeholder={t('products.descriptionPlaceholder')} />
      </div>

      {/* ─── Выбор профиля (тип магазина) ─── */}
      {profileList.length > 0 && (
        <div className="input-group">
          <label>{t('shop.profileLabel')}</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {profileList.map(p => {
              const active = p.id === profileId;
              const onMods = MODULES
                .filter(m => {
                  const v = p.moduleConfig && p.moduleConfig[m.key];
                  return v === undefined || v === null ? true : !!v;
                })
                .map(m => t(m.labelKey));
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProfileId(p.id)}
                  style={{
                    textAlign: 'left', padding: '12px 14px', borderRadius: 'var(--r-md, 12px)',
                    border: '1.5px solid ' + (active ? 'var(--brand-primary)' : 'var(--border)'),
                    background: active ? 'var(--brand-primary-soft)' : 'var(--surface)',
                    cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start',
                  }}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: 'var(--r-sm)', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: active ? 'var(--brand-primary)' : 'var(--bg-subtle)',
                    color: active ? '#fff' : 'var(--fg-muted)',
                  }}>
                    <Icon name={p.icon || 'store'} size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>{p.name}</p>
                    {p.description && (
                      <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>{p.description}</p>
                    )}
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
                      {t('shop.profileModules')}: {onMods.join(', ')}
                    </p>
                  </div>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                    border: '2px solid ' + (active ? 'var(--brand-primary)' : 'var(--border)'),
                    background: active ? 'var(--brand-primary)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {active && <Icon name="check" size={12} color="#fff" />}
                  </div>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 6 }}>
            {t('shop.profileHint')}
          </div>
        </div>
      )}

      {/* ─── Реферальный код (необязательно) ─── */}
      <div className="input-group">
        <label>
          {t('shop.referralCode')}{' '}
          <span style={{ color: 'var(--fg-muted)', fontWeight: 400, fontSize: 12 }}>
            ({t('common.optional')})
          </span>
        </label>
        <input
          ref={refRef}
          className="input"
          placeholder={t('shop.referralCodePlaceholder')}
          style={{ fontFamily: 'monospace', letterSpacing: '0.05em', textTransform: 'uppercase' }}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck="false"
        />
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 6 }}>
          {t('shop.referralCodeHint')}
        </div>
      </div>

      {error && <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{error}</p>}

      <button className="btn btn-primary btn-lg" onClick={create} disabled={creating}>
        {creating ? t('common.saving') : t('shop.createShopButton')}
      </button>

      {ToastView}
    </div>
  );
}

// Базовый массив с ключами локализации.
// Используй useShopNav() в компонентах, чтобы получить уже переведённые подписи.
// Каждый пункт связан с правом (perm). У владельца — все пункты, у сотрудника — только разрешённые.
// Главная и Настройки видны всегда (на главной можно увидеть только то что разрешено,
// в настройках — сменить тему/язык, выйти).
const SHOP_NAV_BASE = [
  { path: '/',           labelKey: 'nav.home',      match: p => p === '/',                 iconName: 'home',     perm: null,        module: null },
  { path: '/products',   labelKey: 'nav.products',  match: p => p.startsWith('/products') || p.startsWith('/categories'), iconName: 'package',  perm: 'products',  module: 'products' },
  { path: '/cashier',    labelKey: 'nav.cashier',   match: p => p.startsWith('/cashier'),  iconName: 'cashier',  perm: 'cashier',   module: 'cashier' },
  { path: '/report-journal',  labelKey: 'nav.report',  match: p => p.startsWith('/report-journal') || p.startsWith('/report') || p.startsWith('/shop/reports'), iconName: 'fileText', perm: 'analytics', module: 'analytics' },
  { path: '/settings',   labelKey: 'nav.settings',  match: p => p.startsWith('/settings'), iconName: 'settings', perm: null,        module: null },
];

// Хук, возвращающий переведённые элементы для BottomNav (отфильтрованные по правам)
export function useShopNav() {
  // eslint-disable-next-line
  const { t, i18n } = useTranslation();
  const { can, isOwner } = useAuth();
  const shop = useMyShop();
  return SHOP_NAV_BASE
    .filter(item => {
      // Модульные пункты — через единую проверку (тумблер + право + совместимость)
      if (item.module) return canSeeModule(shop, item.module, { isOwner, can });
      if (!item.perm) return true;        // home/settings — всегда
      if (isOwner) return true;            // владелец — всё
      return can(item.perm);               // сотрудник — только разрешённое
    })
    .map(item => ({ ...item, label: t(item.labelKey) }));
}

// Совместимость: статический массив с русскими подписями.
// Использовать только если не нужна локализация (например, выход за пределы React).
// В компонентах — useShopNav().
export const SHOP_NAV = SHOP_NAV_BASE.map(item => ({
  ...item,
  label: { 'nav.home': 'Главная', 'nav.products': 'Склад', 'nav.cashier': 'Касса', 'nav.analytics': 'Отчёты', 'nav.settings': 'Настройки' }[item.labelKey],
}));
